'use client';

import { useEffect, useMemo } from 'react';

import { calculateNextDate, processDailyBonusBlocks } from '@/lib/dailyBonusUtils';
import { EBlockType } from '@/lib/enums';
import { IBlock, IDailyBonusItem } from '@/lib/types';
import { useDailyBonusState } from '@/state/hooks/dailyBonus.state.hook';
import { usePopupState } from '@/state/hooks/popup.state.hook';

interface IUseDailyBonusProcessingProps {
  rawBlocks: IBlock[] | null;
  isLoggedIn: boolean;
}

interface IUseDailyBonusProcessingReturn {
  dailyBonusBlocks: IDailyBonusItem[];
}

/**
 * Hook that derives daily bonus blocks from popup offers and syncs them to global state.
 * Computes daily bonus blocks, next available date, and next collection date as derived values,
 * then syncs to global Observable state for the timer hook.
 *
 * @example
 * const { dailyBonusBlocks } = useDailyBonusProcessing({
 *   rawBlocks,
 *   isLoggedIn: true,
 * });
 */
export const useDailyBonusProcessing = ({
  rawBlocks,
  isLoggedIn,
}: IUseDailyBonusProcessingProps): IUseDailyBonusProcessingReturn => {
  const { popupOffers, popUpStartHourUTC } = usePopupState();
  const {
    setDailyBonusBlocks,
    setDailyBonusBlocksNextDate,
  } = useDailyBonusState();

  const dailyBonusBlocks = useMemo((): IDailyBonusItem[] => {
    if (!isLoggedIn || !popupOffers || popupOffers.length === 0) {
      return [];
    }

    const hasDailyBonusInRawBlocks = rawBlocks?.some(
      (block) => block.type === EBlockType.DAILY_BONUS
    );

    if (!hasDailyBonusInRawBlocks) {
      return [];
    }

    return processDailyBonusBlocks(popupOffers);
  }, [popupOffers, isLoggedIn, rawBlocks]);

  const nextAvailableDate = useMemo((): Date | null => {
    if (popUpStartHourUTC == null) {
      return null;
    }

    const currentDate = new Date();
    const nextDate = new Date(currentDate);
    nextDate.setUTCHours(popUpStartHourUTC, 0, 0);

    if (nextDate.getTime() < currentDate.getTime()) {
      nextDate.setUTCDate(nextDate.getUTCDate() + 1);
    }

    return nextDate;
  }, [popUpStartHourUTC]);

  const dailyBonusNextDate = useMemo((): Date | null => {
    if (dailyBonusBlocks.length === 0 || !nextAvailableDate) {
      return null;
    }

    const lastBlock = dailyBonusBlocks[dailyBonusBlocks.length - 1];

    return calculateNextDate(lastBlock, nextAvailableDate);
  }, [dailyBonusBlocks, nextAvailableDate]);

  useEffect(() => {
    setDailyBonusBlocks(dailyBonusBlocks);

    if (dailyBonusNextDate) {
      setDailyBonusBlocksNextDate(dailyBonusNextDate);
    }
  }, [dailyBonusBlocks, dailyBonusNextDate, setDailyBonusBlocks, setDailyBonusBlocksNextDate]);

  return { dailyBonusBlocks };
};
